import React from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Icon from '@mui/material/Icon';
import { useTheme, alpha } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

import { companySettingsApi } from 'core/companySettings';
import { CompanyToolsSection } from 'sectors/pilates/settings/tabs/company/CompanyToolsSection';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';

const SETTINGS_KEY = 'solis:companySettings:v1';

function pickToolsFromMy(res) {
  const my = res?.data ?? res ?? {};
  const tools = my?.sectorSettings?.tools ?? {}; // ✅ doğru yer

  return {
    calendarType: tools?.calendarType ?? 'TAM_SAAT',
    dayStartHour: Number.isFinite(Number(tools?.dayStartHour)) ? Number(tools.dayStartHour) : 8,
    dayEndHour: Number.isFinite(Number(tools?.dayEndHour)) ? Number(tools.dayEndHour) : 22,
  };
}

export function CalendarToolsModal({ open, onClose, onSaved }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const paperBg = isDark ? alpha('#0b1220', 0.72) : alpha('#ffffff', 0.78);
  const border = isDark ? alpha('#fff', 0.12) : alpha('#0b1220', 0.12);

  const [toolsLoading, setToolsLoading] = React.useState(false);
  const [toolsSaving, setToolsSaving] = React.useState(false);
  const [toolsError, setToolsError] = React.useState(null);
  const [toolsEditMode, setToolsEditMode] = React.useState(false);

  const [serverTools, setServerTools] = React.useState({
    calendarType: 'TAM_SAAT',
    dayStartHour: 8,
    dayEndHour: 22,
  });

  const [draftTools, setDraftTools] = React.useState(serverTools);

  const dirty =
    draftTools.calendarType !== serverTools.calendarType ||
    Number(draftTools.dayStartHour) !== Number(serverTools.dayStartHour) ||
    Number(draftTools.dayEndHour) !== Number(serverTools.dayEndHour);

  // ✅ modal açılınca gerçek veriyi çek
  React.useEffect(() => {
    if (!open) return;

    let alive = true;
    (async () => {
      try {
        setToolsLoading(true);
        setToolsError(null);

        const res = await companySettingsApi.getMy();
        const next = pickToolsFromMy(res);

        if (!alive) return;
        setServerTools(next);
        setDraftTools(next);
        setToolsEditMode(false);
      } catch (e) {
        if (!alive) return;
        setToolsError(e?.message || 'Ayarlar yüklenemedi');
      } finally {
        if (alive) setToolsLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [open]);

  const onEdit = () => {
    setDraftTools(serverTools);
    setToolsEditMode(true);
  };

  const onCancel = () => {
    setDraftTools(serverTools);
    setToolsEditMode(false);
    setToolsError(null);
  };

  const onSave = async () => {
    try {
      setToolsSaving(true);
      setToolsError(null);

      const payload = {
        calendarType: draftTools.calendarType,
        dayStartHour: Number(draftTools.dayStartHour),
        dayEndHour: Number(draftTools.dayEndHour),
      };
      // ✅ cache'i update et (reload yok)
      const raw = localStorage.getItem(SETTINGS_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      const prev = parsed?.value || {};

      const next = {
        ...prev,
        sectorSettings: {
          ...(prev.sectorSettings || {}),
          tools: {
            ...(prev.sectorSettings?.tools || {}),
            ...payload,
          },
        },
      };

      localStorage.setItem(SETTINGS_KEY, JSON.stringify({ ts: Date.now(), value: next }));

      // ✅ dinleyen component'lere haber ver
      window.dispatchEvent(new Event('solis:companySettings:changed'));

      await companySettingsApi.updateMyTools(payload);

      // ✅ cache temizle
      localStorage.removeItem(SETTINGS_KEY);

      // ✅ parent dinlemek isterse (reload öncesi)
      onSaved?.(payload);

      // ✅ tek reload
    } catch (e) {
      setToolsError(e?.message || 'Kaydedilemedi');
    } finally {
      setToolsSaving(false);
    }
  };

  const toolsProps = {
    loading: toolsLoading,
    error: toolsError,

    calendarType: draftTools.calendarType,
    setCalendarType: (v) => setDraftTools((p) => ({ ...p, calendarType: v })),

    dayStartHour: Number(draftTools.dayStartHour),
    setDayStartHour: (v) => setDraftTools((p) => ({ ...p, dayStartHour: Number(v) })),

    dayEndHour: Number(draftTools.dayEndHour),
    setDayEndHour: (v) => setDraftTools((p) => ({ ...p, dayEndHour: Number(v) })),

    dirty,
    onEdit,
    onCancel,
    onSave,
    editMode: toolsEditMode,
    saving: toolsSaving,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${border}`,
          backgroundColor: paperBg,
          backdropFilter: 'blur(16px) saturate(140%)',
          boxShadow: isDark ? '0 28px 80px rgba(0,0,0,0.55)' : '0 28px 80px rgba(15,23,42,0.22)',
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: isDark ? 'rgba(0,0,0,0.55)' : 'rgba(2,6,23,0.35)',
          backdropFilter: 'blur(6px)',
        },
      }}
    >
      {/* Header */}
      <MDBox
        sx={{
          px: 2,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${border}`,
        }}
      >
        <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MDBox
            sx={{
              width: 34,
              height: 34,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              background: isDark ? alpha('#fff', 0.08) : alpha('#0b1220', 0.06),
              border: `1px solid ${border}`,
            }}
          >
            <Icon fontSize="small">settings</Icon>
          </MDBox>

          <MDBox>
            <MDTypography fontWeight="bold" sx={{ lineHeight: 1.1 }}>
              Takvim Ayarları
            </MDTypography>
            <MDTypography variant="caption" sx={{ opacity: 0.75 }}>
              Slot tipi ve gün başlangıç/bitiş saatleri
            </MDTypography>
          </MDBox>
        </MDBox>

        <IconButton onClick={onClose} size="small" sx={{ borderRadius: 2 }}>
          <Icon fontSize="small">close</Icon>
        </IconButton>
      </MDBox>

      {/* Body */}
      {/* Body */}
      <MDBox sx={{ p: 2, maxHeight: '72vh', overflow: 'auto' }}>
        <CompanyToolsSection {...toolsProps} />
        {/* 🔒 Yuvarlama (şimdilik pasif) */}
        <MDBox
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            border: `1px dashed ${border}`,
            opacity: 0.6,
          }}
        >
          <MDTypography fontWeight="bold" sx={{ mb: 0.5 }}>
            Seans Yuvarlama Yönü
          </MDTypography>

          <FormControl fullWidth size="small" disabled>
            <Select value="FLOOR">
              <MenuItem value="FLOOR">Aşağı Yuvarla (yarım saat)</MenuItem>
              <MenuItem value="CEIL">Yukarı Yuvarla</MenuItem>
              <MenuItem value="NEAREST">En Yakın Slot</MenuItem>
            </Select>
          </FormControl>

          <MDTypography variant="caption" sx={{ mt: 0.8, display: 'block', opacity: 0.7 }}>
            Yakında eklenecek: Mevcut seansların yeni takvim tipine otomatik taşınması ve yuvarlama
            seçimi.
          </MDTypography>
        </MDBox>
      </MDBox>
    </Dialog>
  );
}

CalendarToolsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
};
